/**
 * Список задач дня.
 *
 * У няни каждая строка — действие в один тап («Выполнено»), у родителя —
 * просмотр и удаление. Просроченная задача подсвечивается, но без упрёка:
 * это напоминание, а не выговор.
 */

import clsx from 'clsx'

import type { ScheduleTask } from '@/shared/api/schedule'
import { Icon } from '@/shared/ui/Icon'
import { taskIcon } from '@/shared/ui/icon-map'
import { Card, Chip } from '@/shared/ui/primitives'
import { EmptyState } from '@/shared/ui/states'

export function TaskList({
  tasks,
  canComplete,
  busyId,
  onComplete,
  onSkip,
  onDelete,
  emptyTitle = 'На этот день задач нет',
  emptyDescription,
  emptyAction,
}: {
  tasks: ScheduleTask[]
  canComplete: boolean
  busyId?: number | null
  onComplete?: (task: ScheduleTask) => void
  onSkip?: (task: ScheduleTask) => void
  onDelete?: (task: ScheduleTask) => void
  emptyTitle?: string
  emptyDescription?: string
  emptyAction?: React.ReactNode
}) {
  if (tasks.length === 0) {
    return (
      <EmptyState
        icon="nav-schedule"
        title={emptyTitle}
        description={emptyDescription}
        action={emptyAction}
      />
    )
  }

  return (
    <div className="space-y-2">
      {tasks.map((task) => (
        <TaskRow
          key={task.id}
          task={task}
          canComplete={canComplete}
          busy={busyId === task.id}
          onComplete={onComplete}
          onSkip={onSkip}
          onDelete={onDelete}
        />
      ))}
    </div>
  )
}

function TaskRow({
  task,
  canComplete,
  busy,
  onComplete,
  onSkip,
  onDelete,
}: {
  task: ScheduleTask
  canComplete: boolean
  busy?: boolean
  onComplete?: (task: ScheduleTask) => void
  onSkip?: (task: ScheduleTask) => void
  onDelete?: (task: ScheduleTask) => void
}) {
  const done = task.status === 'done'
  const skipped = task.status === 'skipped'

  return (
    <Card className={clsx('enter', done && 'opacity-70')}>
      <div className="flex items-start gap-3">
        <div className="flex w-12 shrink-0 flex-col items-center">
          <span className="tabular text-[15px] font-semibold">
            {task.time_start.slice(0, 5)}
          </span>
          {/* Значок берём по виду задачи, а не из task.emoji: тот принадлежит боту. */}
          <Icon name={taskIcon(task.kind)} size={18} className="mt-0.5" />
        </div>

        <div className="min-w-0 flex-1">
          <p className={clsx('text-body font-semibold', done && 'line-through')}>
            {task.title}
          </p>
          {task.place && (
            <p className="text-text-muted text-caption leading-snug">{task.place}</p>
          )}
          {task.note && (
            <p className="text-text-muted mt-1 text-caption leading-snug">{task.note}</p>
          )}

          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            {done && <Chip tone="success">выполнено</Chip>}
            {skipped && <Chip tone="neutral">пропущено</Chip>}
            {!done && !skipped && task.is_overdue && <Chip tone="warm">пора</Chip>}
            {task.series_id && <Chip tone="neutral">повтор</Chip>}
          </div>

          {task.skip_reason && (
            <p className="text-text-muted mt-1.5 text-caption leading-snug">
              {task.skip_reason}
            </p>
          )}

          {!done && !skipped && (canComplete || onDelete) && (
            <div className="mt-3 flex gap-2">
              {canComplete && onComplete && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onComplete(task)}
                  className="press bg-accent tap-target rounded-[var(--radius-button)] px-4 text-note font-medium text-on-accent disabled:opacity-60"
                >
                  Выполнено
                </button>
              )}
              {canComplete && onSkip && (
                <button
                  type="button"
                  onClick={() => onSkip(task)}
                  className="press text-text-muted tap-target rounded-[var(--radius-button)] px-3 text-note"
                >
                  Не получилось
                </button>
              )}
              {!canComplete && onDelete && (
                <button
                  type="button"
                  onClick={() => onDelete(task)}
                  className="press text-text-muted tap-target rounded-[var(--radius-button)] px-3 text-note"
                >
                  Удалить
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}
